import type { CelebrationItem, CelebrationPresentationMeta, CelebrationTier, NextMilestoneHint } from './celebration.types';

type SellerCelebrationPolicyEntry = {
  tier: CelebrationTier;
  whyItMatters: string;
  primaryCtaLabel: string;
  primaryCtaHref?: string;
  secondaryCtaLabel?: string;
  eyebrow?: string;
};

export const SELLER_CELEBRATION_POLICY: Record<string, SellerCelebrationPolicyEntry> = {
  PROFILE_COMPLETE: {
    tier: 1,
    eyebrow: 'Store trust',
    whyItMatters: 'Buyers are more likely to purchase when your store looks complete and professional.',
    primaryCtaLabel: 'Publish your first listing',
    primaryCtaHref: '/seller/designs',
    secondaryCtaLabel: 'Back to dashboard',
  },
  FIRST_STORE_VISIT: {
    tier: 1,
    eyebrow: 'Traffic',
    whyItMatters: 'Someone found your storefront — keep sharing your store link to build momentum.',
    primaryCtaLabel: 'View store analytics',
    primaryCtaHref: '/seller/store',
  },
  WEEKLY_CHALLENGE_COMPLETE: {
    tier: 1,
    eyebrow: 'Weekly win',
    whyItMatters: 'Consistent seller activity compounds into better catalog health and more sales.',
    primaryCtaLabel: 'See this week’s focus',
    primaryCtaHref: '/seller/progress',
  },
  HOT_SELLER_WEEK: {
    tier: 1,
    eyebrow: 'Sales momentum',
    whyItMatters: 'A strong week signals product–market fit — double down on what sold.',
    primaryCtaLabel: 'Review analytics',
    primaryCtaHref: '/seller/analytics',
  },
  QUALITY_LISTER: {
    tier: 1,
    eyebrow: 'Listing quality',
    whyItMatters: 'High-quality listings convert better and earn more visibility in the shop.',
    primaryCtaLabel: 'Review your designs',
    primaryCtaHref: '/seller/designs',
  },
  EARNINGS_100: {
    tier: 1,
    eyebrow: 'Earnings',
    whyItMatters: 'Your shop is generating real gross profit — keep growing your catalog.',
    primaryCtaLabel: 'View earnings',
    primaryCtaHref: '/seller/earnings',
  },
  FIVE_LISTINGS: {
    tier: 2,
    eyebrow: 'Catalog growth',
    whyItMatters: 'A broader catalog gives buyers more reasons to shop your store.',
    primaryCtaLabel: 'Add another design',
    primaryCtaHref: '/seller/design-tool',
  },
  TEN_LISTINGS: {
    tier: 2,
    eyebrow: 'Catalog depth',
    whyItMatters: 'Ten live listings puts you in serious seller territory for discovery.',
    primaryCtaLabel: 'Manage catalog',
    primaryCtaHref: '/seller/designs',
  },
  TEN_SALES: {
    tier: 2,
    eyebrow: 'Repeat demand',
    whyItMatters: 'Ten paid orders prove buyers want your designs — optimize top sellers.',
    primaryCtaLabel: 'See order trends',
    primaryCtaHref: '/seller/analytics',
  },
  STREAK_4_WEEKS: {
    tier: 2,
    eyebrow: 'Consistency',
    whyItMatters: 'Stores that list regularly stay fresh in search and buyer memory.',
    primaryCtaLabel: 'Keep the streak going',
    primaryCtaHref: '/seller/designs',
  },
  EARNINGS_500: {
    tier: 2,
    eyebrow: 'Earnings tier',
    whyItMatters: 'You crossed a meaningful profit threshold — reinvest in your best performers.',
    primaryCtaLabel: 'Open wallet',
    primaryCtaHref: '/seller/earnings',
  },
  EARNINGS_1000: {
    tier: 2,
    eyebrow: 'Earnings tier',
    whyItMatters: 'Four figures in lifetime profit — your store is a real revenue line.',
    primaryCtaLabel: 'View earnings breakdown',
    primaryCtaHref: '/seller/earnings',
  },
  STORE_LAUNCHED: {
    tier: 3,
    eyebrow: 'Launch milestone',
    whyItMatters: 'Your seller account is live — the next step is getting products in front of buyers.',
    primaryCtaLabel: 'Create your first design',
    primaryCtaHref: '/seller/design-tool',
  },
  FIRST_LISTING: {
    tier: 3,
    eyebrow: 'Catalog milestone',
    whyItMatters: 'Your first product is live — share your store so buyers can discover it.',
    primaryCtaLabel: 'Share your store',
    primaryCtaHref: '/seller/store',
  },
  FIRST_SALE: {
    tier: 3,
    eyebrow: 'Revenue milestone',
    whyItMatters: 'You have proof of demand — check the order and plan your next listing.',
    primaryCtaLabel: 'View your order',
    primaryCtaHref: '/seller/orders',
  },
  FIFTY_SALES: {
    tier: 3,
    eyebrow: 'Scale milestone',
    whyItMatters: 'Fifty paid orders is strong momentum — review what’s working and expand it.',
    primaryCtaLabel: 'Open analytics',
    primaryCtaHref: '/seller/analytics',
  },
  HUNDRED_SALES: {
    tier: 3,
    eyebrow: 'Top seller',
    whyItMatters: 'One hundred sales is exceptional — you’re operating at a high level.',
    primaryCtaLabel: 'See your growth story',
    primaryCtaHref: '/seller/progress',
  },
  FIRST_PAYOUT: {
    tier: 3,
    eyebrow: 'Cash milestone',
    whyItMatters: 'Money reached your account — your shop is paying you back.',
    primaryCtaLabel: 'Review payout history',
    primaryCtaHref: '/seller/earnings',
  },
  EARNINGS_5000: {
    tier: 3,
    eyebrow: 'Major earnings',
    whyItMatters: 'Five thousand MAD in lifetime profit — a flagship store milestone.',
    primaryCtaLabel: 'View wallet',
    primaryCtaHref: '/seller/earnings',
  },
};

const SELLER_DEFAULT_POLICY: SellerCelebrationPolicyEntry = {
  tier: 2,
  whyItMatters: 'You hit a real store threshold — keep building on this momentum.',
  primaryCtaLabel: 'View seller progress',
  primaryCtaHref: '/seller/progress',
};

export function resolveSellerCelebrationMeta(
  item: CelebrationItem,
  next: NextMilestoneHint,
): CelebrationPresentationMeta {
  const policy = SELLER_CELEBRATION_POLICY[item.code] ?? SELLER_DEFAULT_POLICY;
  return {
    tier: policy.tier,
    eyebrow: policy.eyebrow ?? 'Milestone reached',
    whyItMatters: policy.whyItMatters,
    primaryCtaLabel: policy.primaryCtaLabel,
    primaryCtaHref: policy.primaryCtaHref ?? item.href ?? '/seller/progress',
    secondaryCtaLabel: policy.secondaryCtaLabel ?? 'Continue to dashboard',
    nextMilestoneTitle: next?.title,
    nextMilestoneHint: next?.hint,
    nextMilestoneHref: next?.href,
  };
}

export function resolveSellerNextMilestone(
  locked?: Array<{ title: string; description: string; href: string }> | null,
): NextMilestoneHint {
  const next = locked?.[0];
  if (!next) return null;
  return {
    title: next.title,
    hint: next.description,
    href: next.href,
  };
}
